import dayjs from "dayjs";
import { ActionItemGroupByKey, RELATIONSHIP_TYPE_OPTIONS, TimelineItem } from "./utils";
import { ThemeCategoryKey } from "../interfaces/themes";
import { SxProps, Theme } from "@mui/material";

type TimeRangeValue = "next30Days" | "today" | "lastDay" | "week" | "month" | "yearToDate" | "custom";

export const timeRangeOptions: { value: TimeRangeValue; label: string }[] = [
  { value: "next30Days", label: "Next 30 Days" },
  { value: "today", label: "Today" },
  // { value: "lastDay", label: "Last day" },
  { value: "week", label: "Last 7 days" },
  { value: "month", label: "Last 30 days" },
  { value: "yearToDate", label: "Year to Date" },
  { value: "custom", label: "Custom" },
];
export enum ActionItemStatus {
  Todo = "To Do",
  InProgress = "In Progress",
  Completed = "Done",
}

export enum ConvertTaskAction {
  ACCEPTED = "ACCEPTED",
  DISCARDED = "DISCARDED",
}

export const actionItemStatuses = Object.keys(ActionItemStatus).map((key) => ({
  label: ActionItemStatus[key as keyof typeof ActionItemStatus],
  value: key,
}));

export interface NoteData {
  id: string;
  notes: string;
  interactionId?: string;
  isPrivate: boolean;
  createdBy: string;
  creatorEmail: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  pictureUrl?: string;
}

export interface InteractionCounts {
  rated: number;
  total: number;
  upcomingInteractionId?: string | null;
}

export interface TimeSpent {
  days: number;
  hours: number;
  minutes: number;
}

export enum MeetingType1 {
  External = "External",
  Internal = "Internal",
}

export enum MeetingType2 {
  Group = "Group",
  FocusTime = "Focus Time",
  OneToOne = "1-1",
}

export interface Option {
  readonly label: string;
  readonly value: string;
}

export interface FormData {
  actionName: string;
  dueDate: dayjs.Dayjs | null;
}

export interface Attachment {
  description: string;
  url: string;
  urlError?: boolean;
  path?: string;
  fileName?: string;
  uploadedAt?: string | number | Date;
  isFromApi?: boolean;
}

export enum ActionItemType {
  All = "rhythm,action_item",
  ActionItem = "action_item",
  Rhythm = "rhythm"
}

export enum IntegrationTypes {
  GOOGLE = "google",
  MICROSOFT = "microsoft",
  // SALESFORCE = "salesforce",
  ZOOM = "zoom"
}
//analytics
export enum TimeDistributionFactor {
  TotalMeetingTime = "TotalMeetingTime",
  CompletedActions = "CompletedActions",
  AccountsCovered = "AccountsCovered",
}

export type UserInfo = {
  id: string;
  email: string;
  name: string;
  picture_url: string | null;
  empDept: string;
  managerId: string;
  managerName: string;
  managerEmail: string;
  employeePorId: string;
  managerPorId: string;
};

export type UserTeamData = {
  currentUser: string;
  clickedUserInfo?: UserInfo;
  currentManager?: UserInfo;
  departments: any[];
  subordinates: UserInfo[];
  managerOptions: UserInfo[];
};


export interface ClientStatsType {
  weekStart: string;
  totalClientsMet: number;
  analystTime: number;
  managerTime: number;
  totalMrr: number;
  explicitAgendas: number;
  clientNoShow: number;
  accountNoShow: number;
  totalInteractions: number;
}


export interface StatConfig {
  key: string;
  title: string;
  valueKey: string;
  type: "count" | "currency" | "time";
}

export interface Options {
  label: string;
  value: string;
  avatar?: string;
  labelColour?:string;
  icon?: string;
  color?: string;
  type?: "assignee" | "coach" | "subscribers" |  "delay";
  email?: string;
  entityType?: "user" | "space";
}

export type Tag = { tagId: string; tagName: string };

export interface DateRangeValue {
  preset: string;
  startDate: string;
  endDate: string;
  overdue?: boolean;
}
export interface TaskNotificationItem {
  notificationId: string;
  taskId: string | null;
  taskTitle: string | null;
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
  updatedBy: string | null;
  updatedByPictureUrl: string | null;
  updatedByName: string | null;
  createdAt: string;
  isRead: boolean;
  clientName?: string;
  orgId?: string;
  subOrgId?: string;
  pendingTaskCount?: number;
}

export interface UserNotificationsResponse {
  assigneeNotificationsCount: number;
  dueDateNotificationsCount: number;
  deletedTaskNotificationsCount: number;
  commentMentionNotificationsCount: number;
  redClientRiskNotificationsCount: number;
  statusNotificationsCount: number;
  clientHealthTransitionNotificationsCount: number;

  assigneeNotifications: TaskNotificationItem[];
  dueDateNotifications: TaskNotificationItem[];
  deletedTaskNotifications: TaskNotificationItem[];
  commentMentionNotifications: TaskNotificationItem[];
  redClientRiskNotifications: TaskNotificationItem[];
  statusNotifications: TaskNotificationItem[];
  clientHealthTransitionNotifications: TaskNotificationItem[];
}

export interface Client {
  clientOrgId: string;
  clientName: string;
}

export type ColumnVisibility = {
  description: boolean;
  account: boolean;
  assignee: boolean;
  coach: boolean;
  dueDate: boolean;
  priority: boolean;
  status: boolean;
  actions: boolean;
  subscribe: boolean;
  columnFilter: boolean;
};

export type AccountColumnVisibility = {
  interactions: boolean;
  email: boolean;
}

export type StatusItem = {
  key: string;
  label: string;
  count: number;
};

export interface ChartDataItem {
  key: string;     // unique identifier (id / enum / email)
  label: string;   // UI label
  fullLabel?: string;
  value: number;   // count
  status?: string;
  isDots?: boolean;
  [k: string]: string | number | boolean | null | undefined;
}

interface DashboardClient {
  clientOrgId: string | null;
  clientName: string | null;
  taskCount: number;
}

export interface DashboardPriority {
  priority: string | null;
  taskCount: number;
}

export interface DashboardResource {
  employeePorId: string;
  employeeName?: string;
  employeeEmail?: string;
  taskCount: number;
}

export interface DashBoardDelay {
  delayCategory: string;
  taskCount: number;
}

export interface ActionItemsDashboardResponse {
  clients: DashboardClient[];
  priorities: DashboardPriority[];
  resources: DashboardResource[];
  delays: DashBoardDelay[];
}

export interface SelectedChartBar {
  key: string;
  label: string;
  value: number;
  groupBy: ActionItemGroupByKey;
}

export type FeatureMap = Record<string, boolean>;

export type FeatureStatus = "idle" | "loading" | "ready" | "error";

export const DEFAULT_FEATURE_MAP: FeatureMap = {
  // pages
  accounts_page: false,
  analytics_page: false,
  interactions_page: true,
  tasks_page: true,
  settings_page: true,
  ask_7g_page: false,
  reporting_page: false,
  contacts_page: false,

  // interactions
  my_interaction: true,
  shared_with_me: false,
  shared_by_me: false,

  // tasks
  my_tasks: true,
  all_tasks: false,
  subscribed_tasks: false,
  task_overview: false,
  new_task: false,
  deleted_tasks: false,
  due_date_changed_task: false,
  support_task: false,
  mention_tasks: false,
  complete_mom_tasks: false,
  ai_recommended_tasks: false,
  client_tasks: false,
  status_change_tasks: false,
  account_updates: false,

  // analytics
  interaction_analytics: false,
  task_analytics: false,
  bussiness_analytics: false,
  call_quality_dashboard: false,
  interaction_type_filter: false,
  interaction_analytics_account_tab: false,
  report_analytics: false,

  // accounts
  customers: false,
  prospects: false,
  vendors: false,
  spaces: false,

  // notifications & settings
  in_app_notifications: false,
  notification_settings: false,
  space_settings: false,
  user_settings: true,
  app_settings: false,
  calendar_integrations: false,
  org_management: false,
  demo_video: false,

  // opportunities
  opportunities_page: false,
  opportunities_slideOver: false,
  imp_opportunities_page: false,
  imp_opportunities_slideOver: false,

  ask_7g: false,
  client_ask_7g: false,
  idp_ask_7g: false,
  idp_clipping: false,
  crm_addition: false,
  client_fingertip_metrics: false,
  new_client_overview_page: false,
  new_mdp: false,
  prep_notes: false,
  task_attachments: false,

  calendar_booking: false,
  user_level_bot_setting: false,
  new_accounts_page: false,
  share_interaction: false,
  idp_user_feedback: false,
  share_external: false,
  user_preferences: false,
  auto_refresh_after_deployment: false,
  business_ask7g: false,
  video_download: false,
  interaction_call_stats_transcript_download: false,
  portfolio_health_trend: false,
  template_based_extraction: false,
  share_with_space: false,
  impersonation: false,
  account_imp_tab: false,
  imp_new_accounts_page: false,
  account_imp_customer_tab: false,
  imp_idp_ask_7g: false,
  imp_idp_user_feedback: false,
  imp_client_fingertip_metrics: false,
  imp_template_based_extraction: false,
  imp_share_with_space: false,
  imp_interactions_page: false,
  imp_my_interaction: false,
  imp_enable_rating_column: false,
  export_accounts_data: false,
  export_ai_recommended_tasks_data: false,
  ai_analysis: false,
  enable_rating_column: false,
  idp_assets_hard_delete_button: false,
  imp_idp_assets_hard_delete_button: false,
  accounts_customers_flight_risk: false,
  accounts_slideover_default_interactions_tab: false,
  accounts_themes_tab: false,
  accounts_customers_view_toggle: false,
  accounts_flight_risk_resolve_option: false,
  imp_accounts_flight_risk_resolve_option: false,
  accounts_flight_risk_create_update: false,
  imp_accounts_flight_risk_create_update: false,
  highradius_interaction_columns: false,
  wbr_report_download: false,
  wbr_department_report_download: false,
  no_sentiment_in_rating_column: false,
  client_ask_7g_all_clients: false,
  template_based_extraction_idp: false,
  org_summary_v2: false,
};

export type FeatureKey = keyof typeof DEFAULT_FEATURE_MAP;

/** Impersonation uses a separate backend flag set; everything else stays off. */
export const IMPERSONATION_FEATURE_MAP: Partial<Record<FeatureKey, string>> = {
  accounts_page: "account_imp_tab",
  new_accounts_page: "imp_new_accounts_page",
  customers: "account_imp_customer_tab",
  idp_ask_7g: "imp_idp_ask_7g",
  idp_user_feedback: "imp_idp_user_feedback",
  client_fingertip_metrics: "imp_client_fingertip_metrics",
  auto_refresh_after_deployment: "auto_refresh_after_deployment",
  template_based_extraction: "imp_template_based_extraction",
  share_with_space: "imp_share_with_space",
  impersonation: "impersonation",
  interactions_page: "imp_interactions_page",
  my_interaction: "imp_my_interaction",
  enable_rating_column: "imp_enable_rating_column",
  idp_assets_hard_delete_button: "imp_idp_assets_hard_delete_button",
  accounts_customers_flight_risk: "imp_accounts_customers_flight_risk",
  accounts_slideover_default_interactions_tab: "imp_accounts_slideover_default_interactions_tab",
  accounts_themes_tab: "imp_accounts_themes_tab",
  accounts_customers_view_toggle: "imp_accounts_customers_view_toggle",
  accounts_flight_risk_resolve_option: "imp_accounts_flight_risk_resolve_option",
  accounts_flight_risk_create_update: "imp_accounts_flight_risk_create_update",
  highradius_interaction_columns: "highradius_interaction_columns",
  opportunities_page: "imp_opportunities_page",
  opportunities_slideOver: "imp_opportunities_slideOver",
  no_sentiment_in_rating_column: "no_sentiment_in_rating_column",
  org_summary_v2: "org_summary_v2"
};

export const createAllFalseFeatureMap = (): FeatureMap =>
  Object.keys(DEFAULT_FEATURE_MAP).reduce((acc, featureKey) => {
    acc[featureKey] = false;
    return acc;
  }, {} as FeatureMap);

export const buildImpersonationFeatureMap = (apiData: any[]): FeatureMap => {
  const featureMap = createAllFalseFeatureMap();

  Object.entries(IMPERSONATION_FEATURE_MAP).forEach(([featureKey, impersonationFlag]) => {
    const flagData = apiData.find((item) => item?.featureName === impersonationFlag);
    featureMap[featureKey] = Boolean(flagData?.isActive && flagData?.isAllowedForUser);
  });

  return featureMap;
};

export interface loggedInUserInfo {
  id: string;
  name: string;
  email: string;
  profilePicture: string;
  accessToken: string;
}

export const EMPTY_USER: loggedInUserInfo = {
  id: "",
  name: "",
  email: "",
  profilePicture: "",
  accessToken: "",
};

 export const SCORE_API_TO_UI_MAP: Record<string, string> = {
    positive: "green",
    negative: "red",
    neutral: "yellow",
    not_rated: "not_rated",
  };

 export const SCORE_UI_TO_API_MAP: Record<string, string | undefined> = {
    green: "positive",
    yellow: "neutral",
    red: "negative",
    not_rated: "not_rated",
  };

  export   const SCORE_OPTIONS = [
      { key: "red", label: "Red", color: "#FF4D4D" },
      { key: "yellow", label: "Yellow", color: "#F7B750" },
      { key: "green", label: "Green", color: "#1EC96B" },
      { key: "not_rated", label: "Not Rated", color: "#EFEBFB" },
      { key: "total", label: "Total:", color: "#8A8A9E" },
    ];

    export interface AccountDetails {
      email: string;
      accountId: string;
      orgId: string;
      profileOrgId: string;
      name: string;
      roleId: string;
      isActive: boolean;
      isExternalUser: boolean;
      isImpersonator: boolean;
      pictureUrl?: string;
    }

export interface OrgManagementRow {
  id: string;
  domain: string;
  type: string;
  accountName: string;
  mrr: number | string;
  subOrgId: string;
  startDate?: string;
  endDate?: string;
  accountManagerId?: string;
  accountManagerName?: string;
  accountManagerEmail?: string;
  clientType?: string;
}

export type RelationshipType = (typeof RELATIONSHIP_TYPE_OPTIONS)[number];

export type ActionItemSelectedType = "external" | "internal";

export interface ImpersonationDetails {
  impersonatingUserPorId: string;
  impersonatingUserEmail: string;
  accessType: string;
  isImpersonating: boolean;
}

export type AccountViewMode = "timeline" | "summary";

export interface AccountViewState {
  selectedView?: "org" | "spaces";
  selectedSpaces?: string[];
  selectedUserPorOrgId?: string | null;
  selectedAccountRelationshipType?: string;
  accountRelationshipType?: string;
  flightRisk?: boolean;
  rating?: string[];
  accountViewMode?: AccountViewMode;
  advertisingAccountManagerName?: string[];
  clientDomain?: string;
  clientName?: string;
  clientOrgId?: string;
  subOrgId?: string;
  slideOverParent?: string;
  parentPageIdentifier?: string;
  opportunityNavigationState?: any;
  salesforceAccountId?: string;
  salesforceAccountUrl?: string;
};

export interface MultipartUploadState {
  uploadId: string;
  objectKey: string;
  interactionId: string;
  assetId?: string;
  file: File;
  fileName: string;
  fileSize: number;
  partSize: number;
  parts: {
    partNumber: number;
    uploadUrl: string;
    status: "pending" | "uploading" | "completed";
    eTag?: string;
  }[];
  uploadedBytes: number;
  progress: number;
  status: UploadStatus;
  errorMessage?: string;
};

export const sendFileExtensions = ["csv", "xlsx", "jpg", "jpeg", "png"];

export enum UploadStatus {
  STARTING = "starting",
  UPLOADING = "uploading",
  COMPLETING = "completing",
  COMPLETED = "completed",
  FAILED = "failed",
}

export const ACTIVE_UPLOAD_STATUSES: UploadStatus[] = [
  UploadStatus.STARTING,
  UploadStatus.UPLOADING,
  UploadStatus.COMPLETING,
];

export interface SalesforceReportPresearchItem {
  id: string;
  name?: string;
  email?: string;
}

export interface SalesforceReportConfig {
  stages?: string[];
  stage?: string[];
  ae_teams?: string[];
  ae_team?: string[];
  deal_types?: string[];
  deal_type?: string[];
  record_types?: string[];
  record_type?: string[];
  manager_forecast?: string[];
  manager_forecasts?: string[];
  buyer_modes?: string[] | null;
  buyer_mode?: string[] | null;
  l1_rygs?: string[] | null;
  l1_ryg?: string[] | null;
  opportunity_sources?: string[] | null;
  opportunity_source?: string[] | null;
  close_date_start?: string | null;
  close_date_end?: string | null;
  created_date_start?: string | null;
  created_date_end?: string | null;
  arr_amount?: number | null;
  arr_amount_operator?: "gt" | "lt" | string | null;
  opportunity_ids?: string | string[] | SalesforceReportPresearchItem[] | null;
  market_pod_lead?: string | string[] | SalesforceReportPresearchItem[] | null;
  opportunity_owner?: string | string[] | SalesforceReportPresearchItem[] | null;
  account_ids?: string | string[] | SalesforceReportPresearchItem[] | null;
  india_sp?: string | string[] | SalesforceReportPresearchItem[] | null;
  sp_pod_lead?: string | string[] | SalesforceReportPresearchItem[] | null;
  columns?: Record<string, boolean> | string[];
}

export const ACCOUNT_SUB_TABS = ["customer", "prospect", "vendor", "spaces"];

export interface AccountSummaryThemes {
  totalTagsCount?: number;
  execEscalationCount?: number;
  managerEscalationCount?: number;
  operationalRiskCount?: number;
  clientControllableCount?: number;
  positiveCount?: number;
}

export interface AccountSummaryDetails {
  fingertipExecutiveSummary?: string;
  themes?: AccountSummaryThemes;
}

export interface AccountThemeTagConfig {
  id: string;
  label: string;
  key: keyof Omit<AccountSummaryThemes, "totalTagsCount">;
  themeCategoryKey: ThemeCategoryKey;
  dotColor: string;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  hoverBackgroundColor: string;
  hoverBorderColor: string;
  hoverTextColor: string;
}

export type ActionItemBreadcrumb = {
  id: string;
  title: string;
  dueDate?: string | null;
};

export type SubtaskItem = {
  actionItemId: string;
  title: string;
  assigneeName?: string | null;
  assigneeEmail?: string | null;
  assigneePictureUrl?: string | null;
  dueDate?: string | null;
  status?: string | null;
  isAiGenerated?: boolean;
};

export type InteractionsColumnVisibility = {
  title: boolean;
  sharedDate: boolean;
  start_time: boolean;
  account: boolean;
  opportunityName: boolean;
  duration: boolean;
  actual_duration: boolean;
  meeting_sentiment: boolean;
  abstractiveScore: boolean;
  rating_source: boolean;
  shareIcon: boolean;
};

export type LevelAccessType = "hierarchy" | "client" | "account";

export enum EmailDirection {
  INBOUND = "INBOUND",
  OUTBOUND = "OUTBOUND",
}

export type TimelineDisplayMode = "interactions" | "emails" | "both";
export interface TimelineMarker {
  label: string;
  date: Date;
  align?: "left" | "center" | "right";
}
export interface InteractionsTimelineProps {
  items: TimelineItem[];
  startDate: Date | string;
  endDate: Date | string;
  mode?: TimelineDisplayMode;
  showInteractions?: boolean;
  showEmails?: boolean;
  showLaneLabels?: boolean;
  showDateRow?: boolean;
  showMonthEndMarkers?: boolean;
  monthlyScore?: Record<string, string> | null;
  navigationState?: Record<string, any>;
  onTimelineClick?: () => void;
  isSlideOverOpen?: boolean;
  className?: string;
  sx?: SxProps<Theme>;
}

export enum AllClientsOption {
  ALL_CLIENTS = "ALL_CLIENTS",
}

export type RelationshipTypes = "all" | "prospect" | "vendor" | "partner" | "client";
